import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import jwtDecode, { JwtPayload } from "jwt-decode";

import { useOpenApi } from "lib/openApiContext";

const TOKEN_EXPIRY_SECONDS = 600; // 10 minutes
const LEEWAY_SECONDS = 30; // amount of time we want to leave before we refetch the token
const STALE_TIME_SECONDS = TOKEN_EXPIRY_SECONDS - LEEWAY_SECONDS; // time after which the token is considered stale
const STALE_TIME_MS = STALE_TIME_SECONDS * 1000;

export function isTokenExpired(token: string) {
  const decoded: JwtPayload = jwtDecode(token);
  // The jwt token exp is stated in seconds since epoch
  const expiryDate = decoded.exp || 0;
  // We convert the current time in milliseconds since epock to seconds since epoch
  const currentTime = Date.now() / 1000;
  // We subtract a threashold from the token expiry to give ourselves time to make the request
  // Then we check if the diff is less than 0. If so, then the token is deemed expired.
  return currentTime >= expiryDate - STALE_TIME_SECONDS;
}

export function useAppToken(options?: UseQueryOptions<string, unknown, string, string[]>) {
  const api = useOpenApi();

  const query = useQuery(
    ["appToken"],
    async () => {
      // If there is no current user, we cannot fetch the app token but there are some cases where this is expected
      // (e.g. reports that are not user-specific)
      if (!window.currentUser) {
        console.warn("No current user found, cannot fetch app token");
        return "";
      }
      const data = await api.getAppApiToken();
      return data.token;
    },
    {
      ...options,
      suspense: true, // means that loading state is handled by React Suspense and error state in error boundaries
      staleTime: STALE_TIME_MS,
      cacheTime: TOKEN_EXPIRY_SECONDS * 1000,
      refetchInterval: STALE_TIME_MS, // makes the query refetch the token when it turns stale
    },
  );

  return query;
}
